// Interface for team standings data
export interface TeamStandings {
    teamId: string;
    coachName: string;
    divisionName: string;
    wins: number;
    losses: number;
    draws: number;
    pointsFor: number;
    pointsAgainst: number;
    points: number;
    rank?: number;
}

// Interface for match data
export interface MatchData {
    homeTeamId: string;
    awayTeamId: string;
    results?: {
        homeTeamScore: number;
        awayTeamScore: number;
    };
}

// Interface for week data
export interface WeekData {
    weekName: string;
    matches: MatchData[];
}

// Interface for division data
export interface DivisionData {
    divisionName: string;
    teams: { teamId: string; teamName: string; coachId: string; race: string }[];
}

// Calculate standings for all teams
export const calculateStandings = (
    divisions: DivisionData[],
    weeks: WeekData[],
    leagueCoaches: { coachId: string; coachName: string }[]
): TeamStandings[] => {
    // Get all available teams
    const getAllTeams = (): { teamId: string; coachName: string; divisionName: string }[] => {
        const teams: { teamId: string; coachName: string; divisionName: string }[] = [];
        divisions.forEach(division => {
            division.teams.forEach(team => {
                const coach = leagueCoaches.find(c => c.coachId === team.coachId);
                teams.push({
                    teamId: team.teamId,
                    coachName: `${team.teamName} - ${coach ? coach.coachName : `Coach ${team.coachId}`}`,
                    divisionName: division.divisionName
                });
            });
        });
        return teams;
    };

    const teams = getAllTeams();
    const standings: Record<string, TeamStandings> = {};

    // Initialize all teams with zero stats
    teams.forEach(team => {
        standings[team.teamId] = {
            teamId: team.teamId,
            coachName: team.coachName,
            divisionName: team.divisionName,
            wins: 0,
            losses: 0,
            draws: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            points: 0
        };
    });

    // Process each week's matches to update standings
    weeks.forEach(week => {
        week.matches.forEach(match => {
            if (match.results) {
                const homeTeam = standings[match.homeTeamId];
                const awayTeam = standings[match.awayTeamId];

                if (homeTeam && awayTeam) {
                    const homeScore = match.results.homeTeamScore;
                    const awayScore = match.results.awayTeamScore;

                    // Update points for/against
                    homeTeam.pointsFor += homeScore;
                    homeTeam.pointsAgainst += awayScore;
                    awayTeam.pointsFor += awayScore;
                    awayTeam.pointsAgainst += homeScore;

                    // Determine winner and update records
                    if (homeScore > awayScore) {
                        // Home team wins
                        homeTeam.wins += 1;
                        awayTeam.losses += 1;
                        homeTeam.points += 3;
                    } else if (homeScore < awayScore) {
                        // Away team wins
                        awayTeam.wins += 1;
                        homeTeam.losses += 1;
                        awayTeam.points += 3;
                    } else {
                        // Draw
                        homeTeam.draws += 1;
                        awayTeam.draws += 1;
                        homeTeam.points += 1;
                        awayTeam.points += 1;
                    }
                }
            }
        });
    });

    const standingsArray = Object.values(standings);

    // Sort standings by points (highest first), then by wins, then by points difference
    standingsArray.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.wins !== a.wins) return b.wins - a.wins;
        const aDiff = a.pointsFor - a.pointsAgainst;
        const bDiff = b.pointsFor - b.pointsAgainst;
        return bDiff - aDiff;
    });

    // Add ranks
    standingsArray.forEach((team, index) => {
        team.rank = index + 1;
    });

    return standingsArray;
};

// Calculate standings for a specific division
export const calculateDivisionStandings = (
    divisionName: string,
    divisions: DivisionData[],
    weeks: WeekData[],
    leagueCoaches: { coachId: string; coachName: string }[]
): TeamStandings[] => {
    const allStandings = calculateStandings(divisions, weeks, leagueCoaches);
    return allStandings.filter(team => team.divisionName === divisionName);
};

// Helper function to get team record as string
export const getTeamRecord = (team: TeamStandings): string => {
    return `${team.wins}-${team.losses}-${team.draws}`;
};

// Helper function to get points difference
export const getPointsDifference = (team: TeamStandings): number => {
    return team.pointsFor - team.pointsAgainst;
};

// Helper function to format points difference with sign
export const formatPointsDifference = (team: TeamStandings): string => {
    const diff = getPointsDifference(team);
    return diff > 0 ? `+${diff}` : diff.toString();
}; 